import { Plugin, PluginKey } from '@tiptap/pm/state';
import { ZWSP_RE } from './commands';
import { isCellNode } from './schema';

/**
 * Slash menu (prototype `wkSlashList` / `wkWysiInputH` / `wkSlashPick`,
 * C1-editor.md §1.11), as a small ProseMirror plugin — `@tiptap/suggestion`
 * is not installed and its trigger model differs anyway.
 *
 * Opens on input when the WHOLE caret block is `/` + up to 20 word chars or
 * spaces; any other content closes it. `q` = the lower-cased query; `pick`
 * survives while `q` is unchanged. Keys while open: Escape closes, ArrowUp/
 * Down move (clamped), Enter/Tab pick (only when something matches). Any
 * mousedown in the surface, blur, switching to Markdown or opening the link
 * bar closes it. The menu itself is rendered by React (WikiSlashMenu) from
 * the plugin state.
 */

/** [label, kind, hint] — verbatim, in prototype order. */
export const SLASH_ITEMS = Object.freeze([
    ['Text', 'p', 'just type'],
    ['Heading 1', 'h1', '# '],
    ['Heading 2', 'h2', '## '],
    ['Heading 3', 'h3', '### '],
    ['Bulleted list', 'ul', '- '],
    ['Numbered list', 'ol', '1. '],
    ['Checklist', 'task', '[] '],
    ['Quote', 'quote', '> '],
    ['Note callout', 'note', '> [!NOTE]'],
    ['Warning callout', 'warn', '> [!WARNING]'],
    ['Code block', 'pre', '```'],
    ['Table', 'table', '| a | b |'],
    ['Divider', 'hr', '---'],
    ['Image', 'image', '![alt](url)'],
    ['Video', 'video', 'Loom · YouTube'],
    ['Link', 'link', 'Ctrl/Cmd+K'],
]);

/** Prototype filter: the label (lower-cased) contains the query. */
export function filterSlash(q) {
    const query = String(q || '').toLowerCase();
    return SLASH_ITEMS.filter(([label]) => label.toLowerCase().indexOf(query) > -1);
}

export const wikiSlashKey = new PluginKey('wikiSlash');
/** Transactions carrying this meta (the editor's own loads) never open the menu. */
export const WIKI_LOAD_META = 'wikiLoad';

export const SLASH_CLOSED = Object.freeze({ open: false, q: '', pick: 0, blockPos: -1 });
const SLASH_RE = /^\/([\w ]{0,20})$/;

/** The slash state for a document state (`prev` keeps `pick` while the query is the same). */
export function slashStateFor(state, prev = SLASH_CLOSED) {
    const { $from } = state.selection;
    const parent = $from.parent;
    if ($from.depth < 1 || !parent.isTextblock || parent.type.spec.code || isCellNode(parent)) return SLASH_CLOSED;
    const m = parent.textContent.replace(ZWSP_RE, '').match(SLASH_RE);
    if (!m) return SLASH_CLOSED;
    const q = m[1].toLowerCase();
    return { open: true, q, pick: prev.open && prev.q === q ? prev.pick : 0, blockPos: $from.before() };
}

export function closeSlash(view) {
    const st = wikiSlashKey.getState(view.state);
    if (st && st.open) view.dispatch(view.state.tr.setMeta(wikiSlashKey, { type: 'close' }));
}

/**
 * Pick an item: if the block is still `/…`, empty it (caret at its start),
 * then run the command for `kind` (`runCommand(view, kind)`).
 */
export function pickSlash(view, kind, runCommand) {
    const tr = view.state.tr.setMeta(wikiSlashKey, { type: 'close' });
    const { $from } = tr.selection;
    const parent = $from.parent;
    if ($from.depth >= 1 && parent.isTextblock && /^\/[\w ]*$/.test(parent.textContent.replace(ZWSP_RE, ''))) {
        tr.delete($from.start(), $from.end());
    }
    view.dispatch(tr);
    if (typeof runCommand === 'function') runCommand(view, kind);
    return true;
}

/** `runCommand(view, kind)` runs a picked item (provided by the editor component). */
export function createSlashPlugin({ runCommand } = {}) {
    return new Plugin({
        key: wikiSlashKey,
        state: {
            init: () => SLASH_CLOSED,
            apply(tr, value, _oldState, newState) {
                const meta = tr.getMeta(wikiSlashKey);
                if (meta && meta.type === 'close') return SLASH_CLOSED;
                if (meta && meta.type === 'pick') return { ...value, pick: meta.pick };
                if (tr.docChanged) {
                    if (tr.getMeta(WIKI_LOAD_META)) return SLASH_CLOSED;
                    return slashStateFor(newState, value);
                }
                if (value.open && tr.selectionSet) {
                    const { $from } = newState.selection;
                    if ($from.depth < 1 || $from.before() !== value.blockPos) return SLASH_CLOSED;
                }
                return value;
            },
        },
        props: {
            handleKeyDown(view, event) {
                const st = wikiSlashKey.getState(view.state);
                if (!st || !st.open) return false;
                const items = filterSlash(st.q);
                if (event.key === 'Escape') {
                    closeSlash(view);
                    return true;
                }
                if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                    const pick = Math.max(0, Math.min(items.length - 1, st.pick + (event.key === 'ArrowDown' ? 1 : -1)));
                    view.dispatch(view.state.tr.setMeta(wikiSlashKey, { type: 'pick', pick }));
                    return true;
                }
                if ((event.key === 'Enter' || event.key === 'Tab') && items.length) {
                    const item = items[st.pick] || items[0];
                    return pickSlash(view, item[1], runCommand);
                }
                return false;
            },
            handleDOMEvents: {
                mousedown(view) {
                    closeSlash(view);
                    return false;
                },
                blur(view) {
                    closeSlash(view);
                    return false;
                },
            },
        },
    });
}
